import React, { FC, memo, useCallback, useEffect, useRef } from 'react';

import {
  CharCountStyled,
  FormMessageBoxStyled,
  FormMessageStyled,
} from 'components/ResqlExpert/formItemStyles';
import usePrefix from 'utils/usePrefix';

import { Label } from '../FormField/styles';
import { TextareaStyled, TextAreaComponentStyled } from './styles';
import { CHARS_LIMIT, FORM_MESSAGE_BOX_HEIGHT } from './constants';

const RESIZE_DEBOUNCE_MS = 150;

/** Returns the closest scrollable ancestor, or documentElement as fallback. */
function getScrollParent(el: HTMLElement): HTMLElement {
  let parent = el.parentElement;
  while (parent && parent !== document.documentElement) {
    const { overflow, overflowY } = getComputedStyle(parent);
    if (/auto|scroll/.test(overflow) || /auto|scroll/.test(overflowY)) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return document.documentElement;
}

// Simple TTL cache so we don't run querySelectorAll on every keystroke.
let _stickyContainer: HTMLElement | null = null;
let _stickyHeight = 0;
let _stickyTs = 0;
const STICKY_CACHE_TTL_MS = 800;
const CONTAINER_MID_DIVISOR = 2;

/**
 * Returns the pixel height occupied by sticky/fixed elements pinned to the
 * BOTTOM of the given scroll container (e.g. a sticky save-button bar).
 */
function getStickyBottomHeight(scrollContainer: HTMLElement): number {
  const now = Date.now();
  if (scrollContainer === _stickyContainer && now - _stickyTs < STICKY_CACHE_TTL_MS) {
    return _stickyHeight;
  }

  const containerRect =
    scrollContainer === document.documentElement
      ? { top: 0, bottom: window.innerHeight }
      : scrollContainer.getBoundingClientRect();

  const containerMidY = (containerRect.top + containerRect.bottom) / CONTAINER_MID_DIVISOR;
  let highestStickyTop = containerRect.bottom;

  scrollContainer.querySelectorAll<HTMLElement>('*').forEach((child) => {
    const pos = getComputedStyle(child).position;
    if (pos !== 'sticky' && pos !== 'fixed') return;
    const rect = child.getBoundingClientRect();
    // Only consider elements in the lower half of the container.
    if (rect.top > containerMidY && rect.top < highestStickyTop) {
      highestStickyTop = rect.top;
    }
  });

  const height = Math.max(0, containerRect.bottom - highestStickyTop);
  _stickyContainer = scrollContainer;
  _stickyHeight = height;
  _stickyTs = now;
  return height;
}

interface Props {
  id: string;
  label?: string;
  limit?: number;
  placeholder?: string;
  text: string;
  setText: (text: string) => void;
}

const TextAreaComponent: FC<Props> = ({
  id,
  label,
  limit = CHARS_LIMIT,
  placeholder,
  text,
  setText,
}) => {
  const t = usePrefix('ResqlExpert');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;

    const scrollContainer = getScrollParent(el);
    const isRoot = scrollContainer === document.documentElement;

    // Snapshot the current scroll position BEFORE changing the element height
    // so we can restore it (prevents the container from jumping).
    const savedScrollTop = isRoot ? window.scrollY : scrollContainer.scrollTop;

    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    el.style.overflowY = 'hidden';

    // Restore scroll to prevent the container from jumping after height change.
    if (isRoot) {
      window.scrollTo({ top: savedScrollTop, behavior: 'instant' });
    } else {
      scrollContainer.scrollTop = savedScrollTop;
    }

    // Ensure the cursor (approximated by the textarea's bottom edge when typing
    // at the end) stays visible above any sticky footer inside the container.
    const stickyHeight = getStickyBottomHeight(scrollContainer);
    if (stickyHeight > 0) {
      const containerBottom = isRoot
        ? window.innerHeight
        : scrollContainer.getBoundingClientRect().bottom;
      const visibleBottom = containerBottom - stickyHeight;
      const elBottom = el.getBoundingClientRect().bottom;

      if (elBottom > visibleBottom) {
        const extra = elBottom - visibleBottom;
        if (isRoot) {
          window.scrollTo({
            top: savedScrollTop + extra + FORM_MESSAGE_BOX_HEIGHT,
            behavior: 'instant',
          });
        } else {
          scrollContainer.scrollTop = savedScrollTop + extra + FORM_MESSAGE_BOX_HEIGHT;
        }
      }
    }
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [text, adjustHeight]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    let debounceTimer: ReturnType<typeof setTimeout>;

    const observer = new ResizeObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(adjustHeight, RESIZE_DEBOUNCE_MS);
    });

    observer.observe(el);

    return () => {
      observer.disconnect();
      clearTimeout(debounceTimer);
    };
  }, [adjustHeight]);

  return (
    <TextAreaComponentStyled>
      <Label htmlFor={id}>{label}</Label>
      <TextareaStyled
        ref={textareaRef}
        value={text}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          setText(e.target.value.slice(0, limit))
        }
        placeholder={placeholder}
        maxLength={limit}
        id={id}
      />
      <FormMessageBoxStyled>
        {text.length >= limit ? (
          <FormMessageStyled aria-live="polite">{t('chars_limit_exceeded')}</FormMessageStyled>
        ) : null}
        <CharCountStyled $isLimit={text.length >= limit}>{`${text.length}/${limit}`}</CharCountStyled>
      </FormMessageBoxStyled>
    </TextAreaComponentStyled>
  );
};

export default memo(TextAreaComponent);
